/**
 * /enroll/desktop — the web half of one-click desktop enrollment
 * (docs/tauri-desktop.md "One-click enrollment via localhost callback",
 * amended 2026-06-11). The tray opens this page with `?port=<loopback
 * port>&state=<nonce>`; after sign-in + consent we mint a short-lived
 * single-use enroll token (POST /machines/enroll-token) and hand it to the
 * tray's 127.0.0.1-only listener via a FULL-PAGE navigation — never fetch,
 * and never the long-lived session JWT (it would land in browser history).
 *
 * Entitlement gating ("Entitlement gating + unenroll" §1): before the
 * consent card we pre-flight the PERSONAL machine count vs the plan cap
 * (free = 1 machine, mirroring the plane's entitlements-read.ts
 * planLimits; team tenants are uncapped). At limit → paywall card with
 * inline one-click Unenroll per machine + the /pricing upgrade path; the
 * card re-checks and flows into consent without ever dropping
 * `port`/`state` (we never navigate away). A mint that still 402s (race:
 * two machines enrolling at once) re-renders the paywall.
 *
 * Security invariants (doc §"Security invariants"): `port` is validated
 * numeric 1024-65535 BEFORE any loopback call; `state` must be a sane
 * URL-safe nonce; the enroll token is never logged.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth, setReturnTo, clearReturnTo } from "../hooks/useAuth";
import { MagicLinkCard } from "../components/MagicLinkCard";
import { hasPro } from "../lib/plan";
import {
  deleteMachine,
  getMe,
  getPersonalMachines,
  getTeams,
  mintEnrollToken,
  PlanLimitError,
  type Machine,
  type Team,
} from "../lib/api";
import { timeAgo } from "../lib/format";
import { NSWordmark, Icon, I, MonoChip, Btn, Card } from "../kit";
import "../marketing.css"; // .mkt-magic — the reused MagicLinkCard

/** Free personal machine cap — mirrors the plane's entitlements-read.ts
 *  planLimits (free: 1 machine; pro/lifetime: unlimited). The plane stays
 *  authoritative: the mint runs the same gate and we handle its 402. */
const FREE_MACHINE_LIMIT = 1;

/** Paywall re-check cadence — an upgrade completes in ANOTHER tab
 *  (Stripe Checkout), so this page polls itself back into consent. */
const RECHECK_MS = 5_000;

// ─── Query-param validation (BEFORE any loopback call) ──────────────

type Params =
  | { ok: true; port: number; state: string }
  | { ok: false; reason: string };

function parseParams(search: string): Params {
  const q = new URLSearchParams(search);
  const portRaw = q.get("port") ?? "";
  const state = q.get("state") ?? "";
  if (!/^\d{1,5}$/.test(portRaw)) {
    return { ok: false, reason: "missing or non-numeric port" };
  }
  const port = Number(portRaw);
  if (port < 1024 || port > 65535) {
    return { ok: false, reason: "port out of range (1024-65535)" };
  }
  // The tray's nonce is crypto-random and URL-safe; anything else is a
  // mangled or hostile link.
  if (!state || state.length > 256 || !/^[A-Za-z0-9._~-]+$/.test(state)) {
    return { ok: false, reason: "missing or malformed state nonce" };
  }
  return { ok: true, port, state };
}

// ─── Gate state machine ──────────────────────────────────────────────

type Gate =
  | { kind: "checking" }
  | { kind: "consent" }
  | { kind: "paywall"; machines: Machine[] }
  | { kind: "sent" } // token handed to the loopback — "return to the menubar"
  | { kind: "failed"; message: string };

/* ── Page chrome (same language as the /pricing topbar) ───────────── */

function EnrollTopbar() {
  return (
    <div className="enr-topbar">
      <Link to="/" aria-label="nightswarm.app home">
        <NSWordmark size={17} />
      </Link>
      <MonoChip tone="ghost">enroll this mac</MonoChip>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 20px 64px",
      }}
    >
      <style>{`
        .enr-topbar { width: 100%; max-width: 560px; display: flex; align-items: center; gap: 10px; padding: 18px 0 28px; }
        .enr-col { width: 100%; max-width: 560px; display: flex; flex-direction: column; gap: 14px; }
        .enr-title { display: flex; align-items: center; gap: 10px; font-size: 17px; font-weight: 650; margin: 0 0 6px; }
        .enr-tile { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; background: var(--color-accent-soft); color: var(--color-accent-fg); flex: none; }
        .enr-machine { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-top: 1px solid var(--color-line, rgba(255,255,255,0.08)); }
        .enr-machine:first-of-type { border-top: 0; }
        .enr-field { display: flex; flex-direction: column; gap: 6px; margin: 14px 0; }
        .enr-field select { background: transparent; color: inherit; border: 1px solid var(--color-line, rgba(255,255,255,0.14)); border-radius: 8px; padding: 8px 10px; font: inherit; }
      `}</style>
      <EnrollTopbar />
      <div className="enr-col">{children}</div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────── */

export default function EnrollDesktop() {
  const { status, user } = useAuth();
  const params = useMemo(
    () => parseParams(window.location.search),
    // location.search is stable for the life of this page — we never
    // navigate within it (that's the point: port/state must survive).
    []
  );

  const [gate, setGate] = useState<Gate>({ kind: "checking" });
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [team, setTeam] = useState<string>(""); // "" = personal
  const [busy, setBusy] = useState(false);
  const [unenrolling, setUnenrolling] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Preserve port/state through the magic-link round-trip: /auth/verify
  // redirects to the site ROOT, so stash the full path while signed-out
  // (useAuth's boot consumes it after the jwt-link landing). Clear once
  // signed-in here so a later unrelated sign-in can't be hijacked.
  useEffect(() => {
    if (!params.ok) return;
    if (status === "signed-out") {
      setReturnTo(
        `/enroll/desktop?port=${params.port}&state=${encodeURIComponent(params.state)}`
      );
    } else if (status === "signed-in") {
      clearReturnTo();
    }
  }, [status, params]);

  // Pre-flight (gating §1): personal machine count vs plan cap. Re-fetches
  // /auth/me (not the cached useAuth user) so an upgrade completed in
  // another tab flips the paywall into consent.
  const preflight = useCallback(async () => {
    try {
      const [me, machines] = await Promise.all([
        getMe(),
        getPersonalMachines(),
      ]);
      if (
        hasPro(me.ownedEntitlements) ||
        machines.length < FREE_MACHINE_LIMIT
      ) {
        setGate({ kind: "consent" });
      } else {
        setGate({ kind: "paywall", machines });
      }
    } catch (e) {
      setGate({
        kind: "failed",
        message: e instanceof Error ? e.message : "pre-flight failed",
      });
    }
  }, []);

  useEffect(() => {
    if (status === "signed-in" && params.ok) {
      setGate({ kind: "checking" });
      preflight();
    }
  }, [status, params.ok, preflight]);

  // Optional team picker (mirrors `nightswarmd enroll --team`). Tolerate
  // planes without /teams — the picker just hides.
  useEffect(() => {
    if (status !== "signed-in") return;
    getTeams()
      .then(setTeams)
      .catch(() => setTeams(null));
  }, [status]);

  // Paywall re-check loop — flows back into consent after an unenroll or
  // upgrade WITHOUT navigating (port/state survive in the URL).
  useEffect(() => {
    if (gate.kind !== "paywall") return;
    const id = setInterval(preflight, RECHECK_MS);
    return () => clearInterval(id);
  }, [gate.kind, preflight]);

  const enroll = async () => {
    if (!params.ok || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const token = await mintEnrollToken(team || null);
      // Show the "return to the menubar" copy, then hand the token to the
      // tray's loopback listener via FULL-PAGE navigation (contract §2:
      // window.location.assign, NOT fetch). Loopback http is
      // mixed-content-exempt, and navigation works everywhere anyway.
      setGate({ kind: "sent" });
      window.location.assign(
        `http://127.0.0.1:${params.port}/enroll/callback?state=${encodeURIComponent(params.state)}&token=${encodeURIComponent(token)}`
      );
    } catch (e) {
      if (e instanceof PlanLimitError) {
        // Mint-402 race (gating §2/§3): another machine took the last slot
        // between pre-flight and mint — re-render the paywall gracefully.
        await preflight();
      } else {
        setErr(e instanceof Error ? e.message : "enroll failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const unenroll = async (m: Machine) => {
    const ok = window.confirm(
      `Unenroll "${m.name}"?\n\nIts machine credentials are revoked immediately: the daemon on that Mac disconnects, schedules pinned to it stop being servable, and its running sessions end. The Mac can re-enroll any time.`
    );
    if (!ok) return;
    setUnenrolling(m.machineId);
    setErr(null);
    try {
      // The paywall lists PERSONAL machines — delete on the personal
      // tenant regardless of the dashboard's selected team.
      await deleteMachine(m.machineId, { personal: true });
      await preflight(); // flows into consent — port/state never left the URL
    } catch (e) {
      setErr(e instanceof Error ? e.message : "unenroll failed");
    } finally {
      setUnenrolling(null);
    }
  };

  // ── Invalid link: friendly error, NO loopback call ────────────────
  if (!params.ok) {
    return (
      <Shell>
        <Card>
          <h1 className="enr-title">
            <span className="enr-tile">
              <Icon d={I.x} size={16} />
            </span>
            This enrollment link is invalid
          </h1>
          <p className="hint" style={{ margin: 0 }}>
            The link is missing or carries a malformed parameter (
            {params.reason}), so nothing was sent to this computer. Open{" "}
            <strong>“Set up this Mac…”</strong> from the NightSwarm menubar
            again to get a fresh link.
          </p>
        </Card>
      </Shell>
    );
  }

  // ── Auth gates ─────────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <Shell>
        <p className="hint">Checking your session…</p>
      </Shell>
    );
  }

  if (status === "signed-out") {
    return (
      <Shell>
        <Card>
          <h1 className="enr-title">
            <span className="enr-tile">
              <Icon d={I.cpu} size={16} />
            </span>
            Sign in to enroll this Mac
          </h1>
          <p className="hint" style={{ marginTop: 0 }}>
            The NightSwarm app on this Mac asked to join your swarm. Sign in
            to approve it — the magic link brings you straight back to this
            step.
          </p>
          <MagicLinkCard />
        </Card>
      </Shell>
    );
  }

  // ── Signed in: pre-flight → consent / paywall / sent ──────────────
  let body: ReactNode;
  if (gate.kind === "checking") {
    body = <p className="hint">Checking your plan and machines…</p>;
  } else if (gate.kind === "failed") {
    body = (
      <Card>
        <h1 className="enr-title">
          <span className="enr-tile">
            <Icon d={I.x} size={16} />
          </span>
          Couldn't check your swarm
        </h1>
        <p className="hint" style={{ margin: 0 }}>
          {gate.message}
        </p>
        <Btn
          size="sm"
          style={{ marginTop: 12 }}
          onClick={() => {
            setGate({ kind: "checking" });
            preflight();
          }}
        >
          Try again
        </Btn>
      </Card>
    );
  } else if (gate.kind === "sent") {
    body = (
      <Card>
        <h1 className="enr-title">
          <span className="enr-tile">
            <Icon d={I.check} size={16} />
          </span>
          Return to the menubar
        </h1>
        <p className="hint" style={{ margin: 0 }}>
          Enrollment was handed to the NightSwarm app on this Mac — the
          menubar finishes the rest (daemon install, then Full Disk Access).
          This tab should hand off by itself; if nothing happened, the app's
          listener may have timed out — open{" "}
          <strong>“Set up this Mac…”</strong> from the menubar again.
        </p>
      </Card>
    );
  } else if (gate.kind === "paywall") {
    const first = gate.machines[0];
    body = (
      <Card>
        <h1 className="enr-title">
          <span className="enr-tile">
            <Icon d={I.zap} size={16} />
          </span>
          Free includes 1 machine
        </h1>
        <p className="hint" style={{ marginTop: 0 }}>
          Unenroll {first ? <strong>{first.name}</strong> : "a machine"} or
          upgrade to Pro to enroll this Mac. This page re-checks
          automatically — after either, enrollment continues right here.
        </p>
        <div>
          {gate.machines.map((m) => (
            <div key={m.machineId} className="enr-machine">
              <span className="enr-tile">
                <Icon d={I.cpu} size={15} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{m.name}</div>
                <div className="hint" style={{ margin: 0, fontSize: 12 }}>
                  {m.status} · last seen {timeAgo(m.lastSeenAt)}
                </div>
              </span>
              <Btn
                variant="danger"
                size="sm"
                disabled={unenrolling !== null}
                onClick={() => unenroll(m)}
              >
                {unenrolling === m.machineId ? "Unenrolling…" : "Unenroll"}
              </Btn>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {/* New tab on purpose: checkout must not eat this page's
              port/state — the poll above flips us into consent. */}
          <a
            className="ns-btn ns-btn--primary ns-btn--sm"
            href="/pricing"
            target="_blank"
            rel="noreferrer"
          >
            <Icon d={I.zap} size={13} /> Upgrade to Pro
          </a>
          <Btn size="sm" onClick={preflight}>
            Re-check
          </Btn>
        </div>
        {err && (
          <p className="hint" style={{ color: "var(--color-warn)" }}>
            {err}
          </p>
        )}
      </Card>
    );
  } else {
    // consent
    body = (
      <Card>
        <h1 className="enr-title">
          <span className="enr-tile">
            <Icon d={I.cpu} size={16} />
          </span>
          Enroll this Mac into your swarm
        </h1>
        <p className="hint" style={{ marginTop: 0 }}>
          The NightSwarm app on this computer asked to join{" "}
          <strong>{user?.email}</strong>'s swarm. Approving mints a
          single-use token (expires in 10 minutes) and hands it to the app
          over a localhost-only callback — your sign-in session never
          leaves this browser. The app then installs its daemon and the Mac
          appears on your Swarm tab.
        </p>
        {teams && teams.length > 0 && (
          <label className="enr-field">
            <span className="hint" style={{ margin: 0 }}>
              Enroll into (machines enrolled into a team are shared with
              every member):
            </span>
            <select value={team} onChange={(e) => setTeam(e.target.value)}>
              <option value="">Personal</option>
              {teams.map((t) => (
                <option key={t.teamId} value={t.teamId}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <Btn variant="primary" disabled={busy} onClick={enroll}>
          {busy ? "Enrolling…" : "Enroll this Mac"}
        </Btn>
        {err && (
          <p className="hint" style={{ color: "var(--color-warn)" }}>
            {err}
          </p>
        )}
        <p className="hint" style={{ marginBottom: 0, fontSize: 12 }}>
          Only approve if you just clicked “Set up this Mac…” in the
          NightSwarm menubar on this computer.
        </p>
      </Card>
    );
  }

  return <Shell>{body}</Shell>;
}
